import { Component, OnInit, ViewChild, AfterViewInit, ElementRef, NgZone, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { environment } from '../../environments/environment.local';
import { IUpdatePersonalInfo_Request } from '../../Utils/_services/account/account.model';
import { AccountService } from '../../Utils/_services/account/account.service';
import { LeftUiComponent } from '../left-ui/left-ui/left-ui.component';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';



@Component({
  selector: 'app-user-details',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, LeftUiComponent],
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.css'
})
export class UserDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('locationInput') locationInput!: ElementRef;

  formData: any = {
    firstName: '',
    lastName: '',
    age: '',
    gender: '',
    location: ''
  };
  
  resolvedLocation: any = null;
  isSubmitting = false;

  locationSuggestions: any[] = [];
  showSuggestions = false;
  private locationSearchSubject = new Subject<string>();
  private searchSubscription!: Subscription;

  constructor(
    private toastr: ToastrService,
    private router: Router,
    private ngZone: NgZone,
    private accountService: AccountService
  ) {}

  ngOnInit(): void {
    const savedForm = localStorage.getItem('saved-personal-form');
    if (savedForm) {
      const saved = JSON.parse(savedForm);
      Object.assign(this.formData, saved);
    }

    this.searchSubscription = this.locationSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(input => {
      if (!input || input.trim().length === 0) {
        this.locationSuggestions = [];
        this.showSuggestions = false;
        return;
      }

      this.accountService.getMapsAutocomplete(input).subscribe({
        next: (res: any) => {
          const data = res?.data || res;
          if (data && data.predictions) {
            this.locationSuggestions = data.predictions;
            this.showSuggestions = true;
          }
        },
        error: (err) => console.error('Autocomplete error', err)
      });
    });
  }

  ngOnDestroy(): void {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }

  onLocationInput(event: any) {
    this.locationSearchSubject.next(event.target.value);
  }

  selectLocation(prediction: any) {
    this.formData.location = prediction.description;
    this.showSuggestions = false;

    this.accountService.getMapsPlaceDetails(prediction.place_id).subscribe({
      next: (res: any) => {
        const data = res?.data || res;
        if (data && data.result) {
          const place = data.result;
          this.resolvedLocation = {
            city: '',
            state: '',
            country: '',
            address: place.formatted_address || prediction.description,
            fullAddress: place.formatted_address || prediction.description
          };

          place.address_components?.forEach((comp: any) => {
            const types = comp.types;
            if (types.includes('locality')) {
              this.resolvedLocation.city = comp.long_name;
            } else if (types.includes('administrative_area_level_1')) {
              this.resolvedLocation.state = comp.short_name;
            } else if (types.includes('country')) {
              this.resolvedLocation.country = comp.long_name;
            }
          });
        }
      },
      error: (err) => {
        console.error('Place details error', err);
        this.toastr.error('Failed to get location details.');
      }
    });
  }



  setGender(gender: string) {
    this.formData.gender = gender;
  }

  useCurrentLocation() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        this.accountService.getMapsGeocode(lat, lng).subscribe({
          next: (res: any) => {
            const data = res?.data || res;
            if (data && data.results && data.results.length > 0) {
              const place = data.results[0];
              this.formData.location = place.formatted_address;
              this.resolvedLocation = {
                city: '',
                state: '',
                country: '',
                address: place.formatted_address,
                fullAddress: place.formatted_address
              };
              
              place.address_components?.forEach((comp: any) => {
                if (comp.types.includes('locality')) {
                  this.resolvedLocation.city = comp.long_name;
                } else if (comp.types.includes('administrative_area_level_1')) {
                  this.resolvedLocation.state = comp.short_name;
                } else if (comp.types.includes('country')) {
                  this.resolvedLocation.country = comp.long_name;
                }
              });
              
              this.toastr.success('Current location detected successfully.');
            } else {
              this.toastr.error('Failed to get location details.');
            }
          },
          error: (err) => {
            console.error('Geocode error', err);
            this.toastr.error('Failed to get location details.');
          }
        });
      }, (error) => {
        this.ngZone.run(() => {
          this.toastr.error('Geolocation failed. Please check your browser permissions.');
        });
      });
    } else {
      this.toastr.error('Geolocation is not supported by your browser.');
    }
  }

  continueClick(form: NgForm): void {
    if (!this.formData.firstName || !this.formData.lastName || !this.formData.age || !this.formData.gender || !this.formData.location) {
      this.toastr.error('Please fill in all fields', 'Error');
      return;
    }

    if (!this.resolvedLocation) {
      this.resolvedLocation = {
        city: this.formData.location.split(',')[0] || '',
        state: '',
        country: '',
        address: this.formData.location,
        fullAddress: this.formData.location
      };
    }

    const request: IUpdatePersonalInfo_Request = {
      firstName: this.formData.firstName,
      lastName: this.formData.lastName,
      age: parseInt(this.formData.age),
      gender: this.formData.gender,
      location: {
        city: this.resolvedLocation.city,
        state: this.resolvedLocation.state,
        country: this.resolvedLocation.country,
        address: this.resolvedLocation.address,
        fullAddress: this.resolvedLocation.fullAddress || this.resolvedLocation.address
      }
    };

    this.isSubmitting = true;
    this.accountService.updatePersonalInfo(request).subscribe({
      next: (res) => {
        this.isSubmitting = false;
        this.toastr.success('Profile updated successfully', 'Success');
        
        // Update the current user state to reflect registration is complete
        const currentUser = this.accountService.currentUserSource.getValue();
        if (currentUser) {
          currentUser.registrationStep = 4;
          currentUser.registrationCompleted = true;
          this.accountService.setCurrentUser(currentUser);
          localStorage.setItem("user", JSON.stringify(currentUser));
          
          // Redirect to appropriate dashboard based on role
          const role = currentUser.role?.toLowerCase();
          if (role === 'admin' || role === 'super-admin') {
            this.router.navigate(['/admin/dashboard']);
          } else {
            this.router.navigate(['/user/dashboard']);
          }
        } else {
          this.router.navigate(['/auth/login']);
        }
      },
      error: (ex) => {
        this.isSubmitting = false;
        console.error('Update personal info error', ex);
        this.toastr.error(ex.error?.msg || 'Failed to update profile details', 'Error');
      }
    });
  }

  logout(): void {
    this.accountService.logout();
    this.router.navigate(['/auth/login']);
  }
}